// LabourCore brand mark — reusable icon / horizontal / stacked variants.
// Source assets live in /public/brand. Dark-on-light originals plus
// white-ink *-on-dark variants for navy headers and marketing chrome.

import type { CSSProperties, ImgHTMLAttributes } from "react";

export type BrandLogoVariant = "icon" | "horizontal" | "stacked";
export type BrandLogoTone = "auto" | "light" | "dark";

export interface BrandLogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "width" | "height"> {
  /** Visual treatment. Defaults to icon mark. */
  variant?: BrandLogoVariant;
  /**
   * light  → dark navy ink (for light/white surfaces)
   * dark   → white ink + blue accent (for navy/dark surfaces)
   * auto   → light
   */
  tone?: BrandLogoTone;
  /** Pixel height of the rendered mark. Width scales with aspect. */
  height?: number;
  /** Optional explicit width; omit to preserve intrinsic aspect. */
  width?: number;
  className?: string;
  alt?: string;
}

const SRC: Record<BrandLogoVariant, Record<"light" | "dark", string>> = {
  icon: {
    light: "/brand/labourcore-logo-icon.png",
    dark:  "/brand/labourcore-logo-icon-on-dark.png",
  },
  horizontal: {
    light: "/brand/labourcore-logo-horizontal.png",
    dark:  "/brand/labourcore-logo-horizontal-on-dark.png",
  },
  stacked: {
    light: "/brand/labourcore-logo-stacked.png",
    dark:  "/brand/labourcore-logo-stacked-on-dark.png",
  },
};

/** Intrinsic aspect (w/h) after tight crop of source assets. */
const ASPECT: Record<BrandLogoVariant, number> = {
  icon:       780 / 1000,
  horizontal: 2860 / 312,
  stacked:    1850 / 555,
};

const DEFAULT_HEIGHT: Record<BrandLogoVariant, number> = {
  icon:       36,
  horizontal: 28,
  stacked:    56,
};

export function BrandLogo({
  variant = "icon",
  tone = "auto",
  height,
  width,
  className = "",
  alt = "LabourCore",
  style,
  ...rest
}: BrandLogoProps) {
  const resolvedTone: "light" | "dark" = tone === "dark" ? "dark" : "light";
  const h = height ?? DEFAULT_HEIGHT[variant];
  const w = width ?? Math.round(h * ASPECT[variant]);
  const src = SRC[variant][resolvedTone];

  const imgStyle: CSSProperties = {
    height: h,
    width: w,
    objectFit: "contain",
    display: "block",
    ...style,
  };

  return (
    <img
      src={src}
      alt={alt}
      width={w}
      height={h}
      decoding="async"
      className={className}
      style={imgStyle}
      {...rest}
    />
  );
}

/** Convenience: icon + optional wordmark text for light chrome (sidebar etc.). */
export function BrandMark({
  height = 36,
  showWordmark = true,
  tone = "light",
  className = "",
  wordmarkClassName = "font-extrabold text-lc-ink tracking-tight",
}: {
  height?: number;
  showWordmark?: boolean;
  tone?: "light" | "dark";
  className?: string;
  wordmarkClassName?: string;
}) {
  return (
    <span className={`inline-flex items-center gap-2 ${className}`.trim()}>
      <BrandLogo variant="icon" tone={tone} height={height} aria-hidden={showWordmark ? true : undefined} alt={showWordmark ? "" : "LabourCore"} />
      {showWordmark && (
        <span className={wordmarkClassName}>LabourCore</span>
      )}
    </span>
  );
}

export default BrandLogo;
